import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Router, RouterModule } from "@angular/router";
import { SessionStorageService } from "../../services/session-storage.service";
import { CustomsClearanceService } from "../models/customs-clearance-service.model";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { CustomsClearanceEndpointService } from "../clearance.service";
import { CommonModule } from '@angular/common';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { TableModule } from "primeng/table";
import { ButtonModule } from "primeng/button";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';

@Component({
    selector: 'app-clearance-services',
    templateUrl: './clearance-services.component.html',
    styleUrls: ['./clearance-services.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        ToastModule,
        ProgressBarModule,
        TableModule,
        ButtonModule,
        InputTextModule,
        TagModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        CustomsClearanceEndpointService,
        SessionStorageService
    ]
})
export class ClearanceServicesComponent implements OnInit {
    canViewServices = false;
    canCreateService = false;
    canEditService = false;

    customsClearanceServices: CustomsClearanceService[] = [];
    searchTerm = '';
    isLoading: boolean = false;
    first = 0;
    rows = 20;
    totalRecords = 0;

    constructor(
        private clearanceService: CustomsClearanceEndpointService,
        private messageService: MessageService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {
    }

    ngOnInit(): void {
        this.canViewServices = this.permissionService.hasPermission(app_permissions.clearance.services.view);
        this.canCreateService = this.permissionService.hasPermission(app_permissions.clearance.services.create);
        this.canEditService = this.permissionService.hasPermission(app_permissions.clearance.services.edit);

        this.loadData();
    }

    loadData(event?: any) {
        if (!this.canViewServices) {
            this.isLoading = false;
            return;
        }
        this.isLoading = true;
        this.first = event?.first ?? 0;
        this.rows = event?.rows ?? 20;
        let page = event?.first ?? 0;
        page = page / 20;
        let pageSize = event?.rows ?? 20;
        if (pageSize == 0)
            pageSize = 20;
        this.clearanceService.getAllCustomsClearanceServices().subscribe({
            next: (data) => {
                this.customsClearanceServices = data;
                this.totalRecords = data.length;
                this.isLoading = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error loading clearance services'
                });
                this.isLoading = false;
            }
        });
    }

    addClearanceService() {
        if (!this.canCreateService) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to create services.' });
            return;
        }
        this.router.navigate(['/clearance/services/add']);
    }

    editClearanceService(clearanceService: CustomsClearanceService) {
        if (!this.canEditService) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to edit services.' });
            return;
        }
        this.router.navigate(['/clearance/services/edit', clearanceService.serviceID]);
    }

    get visibleServices(): CustomsClearanceService[] {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) return this.customsClearanceServices;
        return this.customsClearanceServices.filter(service =>
            [service.serviceName, service.description]
                .some(value => (value || '').toLowerCase().includes(term))
        );
    }

    get metrics(): PageMetric[] {
        const services = this.customsClearanceServices;
        const variants = services.reduce((sum, service) => sum + (service.variants?.length || 0), 0);
        const buying = services.reduce((sum, service) => sum + Number(service.buyingAmount || 0), 0);
        const selling = services.reduce((sum, service) => sum + Number(service.sellingAmount || 0), 0);
        return [
            { label: 'Services', value: services.length, hint: 'Active catalog', tone: 'info' },
            { label: 'Variants', value: variants, hint: 'Operational options', tone: 'default' },
            { label: 'Avg Buying', value: services.length ? Math.round(buying / services.length) : 0, hint: 'SAR', tone: 'warn' },
            { label: 'Avg Selling', value: services.length ? Math.round(selling / services.length) : 0, hint: 'SAR', tone: 'success' }
        ];
    }

    formatAmount(value?: number): string {
        return new Intl.NumberFormat('en-SA', { style: 'currency', currency: 'SAR' }).format(Number(value || 0));
    }
}
